import React, { useState, useRef, useEffect, useCallback } from 'react';
import { fr } from '../i18n/fr';

/**
 * Quality levels available in the selector.
 * Maps to hls.js level indices.
 */
export type QualityLevel = 'auto' | '1080p' | '720p' | '576p' | '360p' | '144p' | 'audio-only';

export interface QualityOption {
  level: QualityLevel;
  label: string;
  hlsLevelIndex: number; // -1 for auto, -2 for audio-only
  bandwidth?: number;
}

/** Default quality options matching the rendition ladder */
export const QUALITY_OPTIONS: QualityOption[] = [
  { level: 'auto', label: fr.quality.auto, hlsLevelIndex: -1 },
  { level: '1080p', label: '1080p', hlsLevelIndex: 0, bandwidth: 6_000_000 },
  { level: '720p', label: '720p', hlsLevelIndex: 1, bandwidth: 3_000_000 },
  { level: '576p', label: '576p', hlsLevelIndex: 2, bandwidth: 1_500_000 },
  { level: '360p', label: '360p', hlsLevelIndex: 3, bandwidth: 800_000 },
  { level: '144p', label: '144p', hlsLevelIndex: 4, bandwidth: 300_000 },
  { level: 'audio-only', label: fr.quality.audioOnly, hlsLevelIndex: -2 },
];

export interface QualitySelectorProps {
  currentLevel: QualityLevel;
  onQualityChange: (option: QualityOption) => void;
  availableLevels?: QualityOption[];
}

/**
 * QualitySelector — dropdown menu in the ControlBar for selecting video quality.
 *
 * Allows the viewer to choose between:
 * - Auto (ABR-managed)
 * - 1080p, 720p, 576p, 360p, 144p
 * - Audio Only
 *
 * Validates: Req 1.34
 */
export const QualitySelector: React.FC<QualitySelectorProps> = ({
  currentLevel,
  onQualityChange,
  availableLevels = QUALITY_OPTIONS,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleSelect = useCallback(
    (option: QualityOption) => {
      onQualityChange(option);
      setIsOpen(false);
    },
    [onQualityChange]
  );

  const currentOption = availableLevels.find((o) => o.level === currentLevel);
  const displayLabel = currentOption?.label ?? fr.quality.auto;

  return (
    <div ref={containerRef} style={styles.container}>
      <button
        onClick={handleToggle}
        style={styles.button}
        aria-label={fr.quality.label}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        title={fr.quality.label}
      >
        <QualityIcon />
        <span style={styles.buttonLabel}>{displayLabel}</span>
      </button>

      {isOpen && (
        <ul
          role="listbox"
          aria-label={fr.quality.label}
          style={styles.menu}
        >
          {availableLevels.map((option) => (
            <li
              key={option.level}
              role="option"
              aria-selected={option.level === currentLevel}
              onClick={() => handleSelect(option)}
              style={{
                ...styles.menuItem,
                ...(option.level === currentLevel ? styles.menuItemActive : {}),
              }}
            >
              {option.level === currentLevel && (
                <span style={styles.checkmark}>✓</span>
              )}
              <span style={styles.menuItemLabel}>{option.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const QualityIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 00.12-.61l-1.92-3.32a.488.488 0 00-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 00-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96a.49.49 0 00-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58a.49.49 0 00-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
  </svg>
);

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'relative',
    display: 'inline-flex',
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 10px',
    borderRadius: '4px',
    color: '#ffffff',
    cursor: 'pointer',
    transition: 'background 0.2s',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  buttonLabel: {
    fontSize: '0.75rem',
  },
  menu: {
    position: 'absolute',
    bottom: '100%',
    right: 0,
    marginBottom: '8px',
    backgroundColor: 'rgba(20, 20, 20, 0.95)',
    borderRadius: '8px',
    padding: '4px 0',
    minWidth: '140px',
    listStyle: 'none',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
    zIndex: 100,
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 16px',
    cursor: 'pointer',
    color: '#ffffff',
    fontSize: '0.8125rem',
    transition: 'background 0.15s',
    gap: '8px',
  },
  menuItemActive: {
    backgroundColor: 'rgba(0, 107, 43, 0.3)',
    color: '#4caf50',
  },
  checkmark: {
    fontSize: '0.75rem',
    width: '16px',
  },
  menuItemLabel: {
    flex: 1,
  },
};
